/**
 * Camera-facing strips in one draw call per batch: projectile ribbon trails, weapon swing arcs,
 * jagged lightning and beams. Each strip is a polyline (up to `maxPoints`) with per-point width
 * and alpha; the CPU expands it into a triangle strip facing the camera every frame. All
 * buffers are allocated once.
 */
import * as THREE from 'three';

const VERT = `
attribute vec2 aUv; attribute vec4 aColor; attribute vec2 aStyle;
varying vec2 vUv; varying vec4 vCol; varying vec2 vStyle;
void main(){ vUv = aUv; vCol = aColor; vStyle = aStyle; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`;

const FRAG = `
uniform float uFx; uniform float uTime; uniform vec3 uWhite;
varying vec2 vUv; varying vec4 vCol; varying vec2 vStyle;
void main(){
  float across = 1.0 - abs(vUv.y);
  float body = smoothstep(0.0, 0.55, across);
  float core = smoothstep(0.62, 0.95, across) * vStyle.x;
  float flow = vStyle.y > 0.0 ? 0.72 + 0.28 * sin(vUv.x * 9.0 - uTime * vStyle.y) : 1.0;
  vec3 col = mix(vCol.rgb, uWhite, core);
  float a = vCol.a * max(body, core) * flow * uFx;
  if (a < 0.004) discard;
  gl_FragColor = vec4(col, a);
}`;

export class StripBatch {
  readonly mesh: THREE.Mesh;
  /** Points per strip (x, y, z). */
  readonly pts: Float32Array;
  readonly width: Float32Array;
  readonly alpha: Float32Array;
  readonly count: Int32Array;
  /** r, g, b per strip. */
  readonly color: Float32Array;
  /** core amount, flow speed per strip. */
  readonly style: Float32Array;
  private readonly active: Uint8Array;
  private readonly dirtyClear: Uint8Array;
  private readonly freeList: Int32Array;
  private freeTop: number;
  private readonly geo: THREE.BufferGeometry;
  private readonly mat: THREE.ShaderMaterial;
  private readonly aPos: THREE.BufferAttribute;
  private readonly aUv: THREE.BufferAttribute;
  private readonly aCol: THREE.BufferAttribute;
  private readonly aStyle: THREE.BufferAttribute;
  private readonly tmpT = new THREE.Vector3();
  private readonly tmpV = new THREE.Vector3();
  private readonly tmpS = new THREE.Vector3();

  constructor(readonly maxStrips: number, readonly maxPoints: number, uniforms: { uFx: { value: number }; uTime: { value: number } }, additive: boolean, renderOrder: number) {
    const nPts = maxStrips * maxPoints;
    this.pts = new Float32Array(nPts * 3);
    this.width = new Float32Array(nPts);
    this.alpha = new Float32Array(nPts);
    this.count = new Int32Array(maxStrips);
    this.color = new Float32Array(maxStrips * 3);
    this.style = new Float32Array(maxStrips * 2);
    this.active = new Uint8Array(maxStrips);
    this.dirtyClear = new Uint8Array(maxStrips);
    this.freeList = new Int32Array(maxStrips);
    for (let i = 0; i < maxStrips; i++) this.freeList[i] = maxStrips - 1 - i;
    this.freeTop = maxStrips;

    const nV = nPts * 2;
    this.geo = new THREE.BufferGeometry();
    this.aPos = new THREE.BufferAttribute(new Float32Array(nV * 3), 3).setUsage(THREE.DynamicDrawUsage);
    this.aUv = new THREE.BufferAttribute(new Float32Array(nV * 2), 2).setUsage(THREE.DynamicDrawUsage);
    this.aCol = new THREE.BufferAttribute(new Float32Array(nV * 4), 4).setUsage(THREE.DynamicDrawUsage);
    this.aStyle = new THREE.BufferAttribute(new Float32Array(nV * 2), 2).setUsage(THREE.DynamicDrawUsage);
    this.geo.setAttribute('position', this.aPos);
    this.geo.setAttribute('aUv', this.aUv);
    this.geo.setAttribute('aColor', this.aCol);
    this.geo.setAttribute('aStyle', this.aStyle);
    const idx = new (nV > 65535 ? Uint32Array : Uint16Array)(maxStrips * (maxPoints - 1) * 6);
    let k = 0;
    for (let s = 0; s < maxStrips; s++) {
      for (let j = 0; j < maxPoints - 1; j++) {
        const a = (s * maxPoints + j) * 2;
        idx[k++] = a;
        idx[k++] = a + 1;
        idx[k++] = a + 2;
        idx[k++] = a + 1;
        idx[k++] = a + 3;
        idx[k++] = a + 2;
      }
    }
    this.geo.setIndex(new THREE.BufferAttribute(idx, 1));
    this.mat = new THREE.ShaderMaterial({
      uniforms: { uFx: uniforms.uFx, uTime: uniforms.uTime, uWhite: { value: new THREE.Vector3(0.92, 0.95, 1.0) } },
      vertexShader: VERT,
      fragmentShader: FRAG,
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
      blending: additive ? THREE.AdditiveBlending : THREE.NormalBlending,
    });
    this.mesh = new THREE.Mesh(this.geo, this.mat);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = renderOrder;
  }

  alloc(r: number, g: number, b: number, core: number, flow: number): number {
    if (this.freeTop === 0) return -1;
    const id = this.freeList[--this.freeTop]!;
    this.active[id] = 1;
    this.count[id] = 0;
    this.color[id * 3] = r;
    this.color[id * 3 + 1] = g;
    this.color[id * 3 + 2] = b;
    this.style[id * 2] = core;
    this.style[id * 2 + 1] = flow;
    return id;
  }

  free(id: number): void {
    if (id < 0 || !this.active[id]) return;
    this.active[id] = 0;
    this.dirtyClear[id] = 1;
    this.freeList[this.freeTop++] = id;
  }

  setPoint(id: number, j: number, x: number, y: number, z: number, w: number, a: number): void {
    const p = id * this.maxPoints + j;
    this.pts[p * 3] = x;
    this.pts[p * 3 + 1] = y;
    this.pts[p * 3 + 2] = z;
    this.width[p] = w;
    this.alpha[p] = a;
  }

  /** Expand every active strip into camera-facing quads. */
  build(camPos: THREE.Vector3): void {
    const P = this.aPos.array as Float32Array;
    const UV = this.aUv.array as Float32Array;
    const CO = this.aCol.array as Float32Array;
    const ST = this.aStyle.array as Float32Array;
    const M = this.maxPoints;
    let any = false;
    for (let s = 0; s < this.maxStrips; s++) {
      if (!this.active[s]) {
        if (this.dirtyClear[s]) {
          this.dirtyClear[s] = 0;
          for (let j = 0; j < M * 2; j++) CO[((s * M) * 2 + j) * 4 + 3] = 0;
          any = true;
        }
        continue;
      }
      any = true;
      const n = Math.min(M, this.count[s]!);
      const r = this.color[s * 3]!;
      const g = this.color[s * 3 + 1]!;
      const b = this.color[s * 3 + 2]!;
      const core = this.style[s * 2]!;
      const flow = this.style[s * 2 + 1]!;
      for (let j = 0; j < M; j++) {
        const jj = Math.min(j, Math.max(0, n - 1));
        const p = s * M + jj;
        const x = this.pts[p * 3]!;
        const y = this.pts[p * 3 + 1]!;
        const z = this.pts[p * 3 + 2]!;
        const w = j < n ? this.width[p]! : 0;
        const a = j < n && n >= 2 ? this.alpha[p]! : 0;
        // Tangent from neighbours.
        const pa = s * M + Math.max(0, jj - 1);
        const pb = s * M + Math.min(n - 1, jj + 1);
        this.tmpT.set(this.pts[pb * 3]! - this.pts[pa * 3]!, this.pts[pb * 3 + 1]! - this.pts[pa * 3 + 1]!, this.pts[pb * 3 + 2]! - this.pts[pa * 3 + 2]!);
        this.tmpV.set(x - camPos.x, y - camPos.y, z - camPos.z);
        this.tmpS.crossVectors(this.tmpT, this.tmpV);
        const len = this.tmpS.length();
        if (len > 1e-6) this.tmpS.multiplyScalar((w * 0.5) / len);
        else this.tmpS.set(0, w * 0.5, 0);
        const v = (s * M + j) * 2;
        P[v * 3] = x + this.tmpS.x;
        P[v * 3 + 1] = y + this.tmpS.y;
        P[v * 3 + 2] = z + this.tmpS.z;
        P[v * 3 + 3] = x - this.tmpS.x;
        P[v * 3 + 4] = y - this.tmpS.y;
        P[v * 3 + 5] = z - this.tmpS.z;
        const u = n > 1 ? jj / (n - 1) : 0;
        UV[v * 2] = u;
        UV[v * 2 + 1] = 1;
        UV[v * 2 + 2] = u;
        UV[v * 2 + 3] = -1;
        for (let q = 0; q < 2; q++) {
          const c = (v + q) * 4;
          CO[c] = r;
          CO[c + 1] = g;
          CO[c + 2] = b;
          CO[c + 3] = a;
          ST[(v + q) * 2] = core;
          ST[(v + q) * 2 + 1] = flow;
        }
      }
    }
    if (any) {
      this.aPos.needsUpdate = true;
      this.aUv.needsUpdate = true;
      this.aCol.needsUpdate = true;
      this.aStyle.needsUpdate = true;
    }
  }

  clear(): void {
    for (let s = 0; s < this.maxStrips; s++) if (this.active[s]) this.free(s);
  }

  dispose(): void {
    this.mesh.removeFromParent();
    this.geo.dispose();
    this.mat.dispose();
  }
}
